import { EP_LEAD_BUSINESS_DAYS } from './ep-outcome-display'
import type {
  CaseAuditTarget,
  CloseOutState,
  ForecastQueueBand,
  ForecastQueueGroup,
  PlannerResponseState,
  PlannerSource,
  RequiredReadySource,
  SubmitWindowState,
  UnscheduledReason,
} from '@/types/backofficeAnalytics.types'
import type { HealthVariant } from '@/lib/health-tone'
import {
  REPORTING_TIME_ZONE,
  formatBusinessDays,
  formatDays,
  formatReportingDate,
} from '@/lib/formatters'
import { POST_DELIVERY_HOURLY_RATE } from '@/types/backofficeAnalytics.types'

/**
 * Display copy and tone for the Forecasts view (sc-1212). Colocated pure
 * helpers, same precedent as `specialist-view.ts` in this folder — no React,
 * no hooks, just the vocabulary the route and its components share.
 */

export const FORECAST_QUEUE_BAND_LABELS: Record<ForecastQueueBand, string> = {
  AT_RISK: 'At risk',
  START_NOW: 'Start now',
  SCHEDULED: 'Scheduled',
  UNSCHEDULED: 'No meeting booked',
}

/**
 * `lib/health-tone.ts`'s vocabulary, never a hardcoded color (§4). At risk is
 * the alarming one; Start now is a heads-up, not yet a problem; Scheduled
 * with room to spare is the healthy state — the same "pass/fail-plus-pending"
 * reasoning `EpOutcomesPanel` already applies to its own bands (§15).
 */
export const FORECAST_QUEUE_BAND_TONE: Record<
  ForecastQueueBand,
  HealthVariant
> = {
  AT_RISK: 'error',
  START_NOW: 'action',
  SCHEDULED: 'healthy',
  UNSCHEDULED: 'idle',
}

/**
 * "in 12 days" / "3 days ago" / "Today" — `daysToDeadline` is signed
 * (negative once the deadline has passed), so this reads the sign rather
 * than taking an absolute value and losing whether the case is early or
 * late. Returns "—" for `null`, never a guessed date.
 */
export function formatDaysToDeadline(days: number | null): string {
  if (days === null) return '—'
  if (days === 0) return 'Today'
  if (days > 0) return days === 1 ? 'In 1 day' : `In ${days} days`
  const overdue = Math.abs(days)
  return overdue === 1 ? '1 day ago' : `${overdue} days ago`
}

/**
 * The turnaround budget's length, read off the payload
 * (`AnalyticsForecastQueue.budgetBusinessDays`) — or `null` when it is not
 * usable.
 *
 * **The fallback is inverted from `postDeliveryHourlyRate`, and that is
 * deliberate — do not "fix" it back.** The rate must always render a number,
 * because a missing rate would print `$0` beside real hours and say "nothing
 * owed"; a wrong-but-plausible rate is the lesser lie. A missing budget costs
 * nothing: the denominator is dropped ("12 business days left") and every
 * row's count is still correct, because the API counted it. Falling back to a
 * literal here would be worse than dropping the number — it would print a
 * budget the rows were not computed against, which is the exact bug this
 * replaced (the page counted down against 25 for 12 days after the policy
 * moved to 34).
 *
 * `null` means missing, zero, NaN or negative — an API older than the field
 * (web and API deploy separately), or a value that cannot be a length.
 */
export function caseBudgetBusinessDays(
  value: number | null | undefined,
): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return null
  }
  return value
}

/**
 * The business day by which the PLAN must be ready, counted from the
 * data-gathering approval: the turnaround budget less the delivery lead.
 * Mirrors `requiredReadyBy` in the API's
 * `forecast-queue-bands.ts` (`budgetBusinessDays - EP_LEAD_BUSINESS_DAYS`). Null in, null out.
 */
export function requiredReadyBusinessDays(
  budget: number | null,
): number | null {
  if (budget === null) return null
  return budget - EP_LEAD_BUSINESS_DAYS
}

/**
 * How close to spent the budget has to be before it reads as a warning.
 *
 * One business week. A manager scanning the queue should get the heads-up
 * with a full week of runway left, not only once the case is already in
 * trouble.
 *
 * An absolute duration on purpose, so it does NOT scale with the budget. Its
 * share of the budget therefore drifts as the policy moves — it was a fifth of
 * a 25-day budget and is roughly a seventh of a 34-day one — and that is fine,
 * because the percentage was never the point. A full business week of runway is.
 *
 * Deliberately NOT tied to `EP_LEAD_BUSINESS_DAYS`, even though both are 5
 * today. That constant is the delivery lead for a meeting — a different
 * promise on a different clock — and coupling them would mean a change to the
 * delivery policy silently moved when this column turns amber.
 */
const BUDGET_WARNING_THRESHOLD_DAYS = 5

/**
 * "12 of 34 business days left" (or "12 business days left" when the budget
 * is unknown) / "4 business days over" / "Timer not
 * started" / "Approved — start date unknown".
 *
 * `budgetBusinessDaysLeft` is never clamped at zero (see the field's doc
 * comment on `ForecastQueueRow`), so a case 11 days over reads differently
 * from one that just ran out — mirrors `formatDaysToDeadline`'s sign-aware
 * treatment above, for the same reason.
 *
 * **`days === null` covers two different causes, and `budgetStartUnknown` is
 * what tells them apart — never collapse them into one "unknown" word.** The
 * common case is a form that hasn't been approved yet ("Timer not started" —
 * genuinely waiting on the client). The rarer case is a form that WAS
 * approved but whose approval date can't be recovered ("Approved — start
 * date unknown") — that case is not waiting on anyone and must not read like
 * it is. Both get words rather than a dash; a dash would read as missing
 * data on a page where this is the common state.
 */
export function formatBudgetDaysLeft(
  days: number | null,
  budgetStartUnknown: boolean,
  /** From `caseBudgetBusinessDays`. Required with no default: a default of 25
   * is the original bug wearing a type signature. `null` drops the
   * denominator. */
  budgetBusinessDays: number | null,
): string {
  if (days === null) {
    return budgetStartUnknown
      ? 'Approved — start date unknown'
      : 'Timer not started'
  }
  if (days > 0) {
    // "1 of 34 business days left" stays plural — it is the budget that is
    // counted. Only the number-free form is about the single remaining day.
    if (budgetBusinessDays !== null) {
      return `${days} of ${budgetBusinessDays} business days left`
    }
    return `${days} business ${days === 1 ? 'day' : 'days'} left`
  }
  const over = Math.abs(days)
  return `${over} business ${over === 1 ? 'day' : 'days'} over`
}

/**
 * Why this row's countdown says what it says — the `title` on the budget line.
 *
 * `undefined` for the ordinary case, where the timer runs from the
 * data-gathering approval exactly as every other explanation on the page
 * already states. A tooltip there would be noise on 150-odd rows.
 *
 * The three that DO need words:
 *
 * - **No countdown, never approved** — waiting on the client, not the
 *   specialist.
 * - **No countdown, approved** — the approval exists but its instant is one of
 *   the 2026-06-16 activity-log backfill rows, so there is nothing to count
 *   from (11 production rows).
 * - **A countdown anchored somewhere else** — a second-service case. An IE
 *   Pillars or SO ticket is raised by hand against data gathering approved a
 *   median 62 days earlier for the client's Rothology case, so it counts from
 *   the day the ticket was opened instead (14 production rows). Without this
 *   sentence the number reads as a DG-approval countdown and is nine weeks
 *   off.
 *
 * `budgetStartFromTicketCreation` is optional because the API and web halves
 * deploy separately — absent means "say nothing", never "false".
 */
export function budgetAnchorExplanation(
  days: number | null,
  budgetStartUnknown: boolean,
  budgetStartFromTicketCreation?: boolean,
): string | undefined {
  if (days === null) {
    return budgetStartUnknown
      ? 'The data-gathering form was approved, but the approval date wasn’t recorded, so the turnaround timer has no start to count from. This case is not waiting on the client.'
      : 'The turnaround timer starts when the data-gathering form is first approved — that hasn’t happened yet.'
  }
  if (budgetStartFromTicketCreation) {
    return 'This case counts from the day its ticket was opened, not from a data-gathering approval: the client’s data gathering was approved earlier for a different service, so this work could not have started before someone raised the ticket.'
  }
  return undefined
}

/**
 * Tone for `budgetBusinessDaysLeft` — healthy with comfortable runway
 * (more than a business week left), a warning inside that last week, and
 * error once the budget is spent (zero or negative).
 *
 * `null` — whether the timer hasn't started yet or was approved with an
 * unrecoverable start date (`budgetStartUnknown`) — is `idle` in both cases,
 * muted, never green: neither is "the budget is fine", and neither is the
 * specialist's problem to fix. The two causes read differently only in
 * `formatBudgetDaysLeft`'s words, not in tone.
 */
export function budgetDaysLeftTone(days: number | null): HealthVariant {
  if (days === null) return 'idle'
  if (days <= 0) return 'error'
  if (days <= BUDGET_WARNING_THRESHOLD_DAYS) return 'action'
  return 'healthy'
}

/**
 * How many days behind `requiredReadyBy` still reads as a recoverable
 * warning rather than a firm error.
 *
 * Mirrors `BUDGET_WARNING_THRESHOLD_DAYS`'s reasoning at half its width: the
 * projection is already the median (p50, "what's likely"), a softer claim
 * than the p75 behind `safeStartAt`, so it earns a narrower amber band before
 * escalating to red. A case less than a business week behind is still likely
 * to recover with attention; a full business week or more behind is treated
 * the same as a spent budget.
 *
 * Like `BUDGET_WARNING_THRESHOLD_DAYS`, an absolute duration — it does not
 * scale with the budget.
 */
const SLACK_WARNING_THRESHOLD_DAYS = 3

/**
 * "on track — 4 days spare" / "3 days behind" — the copy under the projected
 * ready date. Distinct from `formatBudgetDaysLeft`: slack is signed but the
 * words never repeat the number's sign, "days spare" and "days behind"
 * already carry it.
 */
export function formatSlackDays(
  days: number,
  /**
   * `ForecastQueueRow.projectionLapsed` — the projected date has already gone
   * by. Optional because the API and web halves deploy separately; absent
   * reads as "not lapsed", which is the pre-existing wording.
   */
  projectionLapsed?: boolean,
): string {
  if (days >= 0) {
    const spare =
      days === 0
        ? 'no spare days'
        : `${days} ${days === 1 ? 'day' : 'days'} spare`
    // "On track" is the one thing this row is not. Slack assumes the case is
    // ready the moment you read the page, which is why a case with a
    // three-week-old estimate and no activity scored "on track — no spare
    // days". The figure survives; the reassurance does not.
    return projectionLapsed
      ? `estimate passed — ${spare}`
      : `on track — ${spare}`
  }
  const behind = Math.abs(days)
  return `${behind} ${behind === 1 ? 'day' : 'days'} behind`
}

/**
 * "Due Oct 20 · turnaround budget" — the promise behind a row that has a date
 * but no booked meeting, which is otherwise invisible.
 *
 * **`requiredReadyBy` is not a column.** It decides which section a row lands
 * in and it is read nowhere on the row, so a case with no meeting appeared in
 * Scheduled, said "No meeting booked", and showed nothing to explain what it
 * was scheduled against. Tyler, 2026-10-20: *"if its missing meetings isnt
 * that No Date? But then if no date how is there a projected ready date
 * column?"*
 *
 * Returns null for a MEETING source, where the Deadline column already shows
 * the date, and for an absent source (an older API, or no date at all). So
 * only the ~10 rows that need the explanation grow a line.
 */
export function budgetDueLine(
  requiredReadyBy: string | null,
  source: RequiredReadySource | null | undefined,
  formatDate: (iso: string) => string,
): string | null {
  if (source !== 'BUDGET' || !requiredReadyBy) return null
  return `Due ${formatDate(requiredReadyBy)} · turnaround budget`
}

/**
 * Tone for the Projected Ready column.
 *
 * `null` slack — either the stage has too few samples to project
 * (`projectedReadyAt` is null) or nothing has promised this case a date yet
 * (`requiredReadyBy` is null, i.e. no meeting AND no DG approval) — is
 * `idle`, never green: an unknown is not the same claim as "on track".
 * Any non-negative slack is `healthy`; negative slack escalates from
 * `action` to `error` past `SLACK_WARNING_THRESHOLD_DAYS`, same shape as
 * `budgetDaysLeftTone`.
 */
export function projectedReadyTone(
  slackDays: number | null,
  /** `ForecastQueueRow.projectionLapsed`; absent reads as not lapsed. */
  projectionLapsed?: boolean,
): HealthVariant {
  if (slackDays === null) return 'idle'
  // A forecast that has expired cannot be the healthy state, however much
  // room is left against the required date — the room is only there if the
  // case finishes today, and the expired estimate is the evidence it will not.
  // `action` rather than `error`: it still MIGHT land, and the rows that
  // genuinely cannot keep the red below.
  if (projectionLapsed) return slackDays >= 0 ? 'action' : 'error'
  if (slackDays >= 0) return 'healthy'
  if (Math.abs(slackDays) <= SLACK_WARNING_THRESHOLD_DAYS) return 'action'
  return 'error'
}

/**
 * The floor below which an owner's own median Plan Work is more noise than
 * signal, and the line under their name is suppressed entirely rather than
 * shown with a caveat.
 *
 * 5, mirroring `MIN_OFFSET_SAMPLE`'s reasoning on the API side (a stage
 * offset below its floor publishes no number at all rather than a shaky
 * one). On production two of five owners sit at n=3 and n=1 — a "median" of
 * one case is just that one case's duration wearing a bigger word, and
 * showing it beside owners with n=250+ would read as comparable when it
 * isn't.
 */
export const MIN_OWNER_PLAN_WORK_SAMPLE = 5

/** Whether `ownerPlanWorkSampleSize` clears the floor above. */
export function hasReliableOwnerPlanWorkRate(sampleSize: number): boolean {
  return sampleSize >= MIN_OWNER_PLAN_WORK_SAMPLE
}

/**
 * `ForecastQueueGroup` display copy (sc-1212 round 2) — the three section
 * headings, in section order. "Urgent" leads with WHERE it stands (past the
 * promised date) rather than a bare "Overdue," because this is the section
 * the page exists for and the heading should say what happened, not just
 * flag severity. "No date promised" over "Undated"/"Unscheduled" — it says
 * WHY the case has no date (nothing has promised it one yet), not just that
 * a field is empty.
 */
export const FORECAST_QUEUE_GROUP_LABELS: Record<ForecastQueueGroup, string> = {
  URGENT: 'Urgent — past the promised date',
  SCHEDULED: 'Scheduled',
  UNDATED: 'No date promised',
  // "Parked" over "Muted"/"Snoozed": the manager has not silenced a problem,
  // they have judged that nothing can move it yet and said when to look again.
  // The heading carries that reason so the section cannot read as a place
  // where awkward rows go to be forgotten.
  PARKED: 'Parked — nothing we can do yet',
}

/**
 * `row.waitingOnDataGathering`'s badge copy — a flag on the row, never a
 * section (see the field's doc comment on `ForecastQueueRow`). Phrased as
 * the blocker itself ("waiting on data gathering") rather than an
 * instruction, since the badge's whole job is to redirect WHO acts — a
 * planner needs to chase the approval, not the specialist assigned to the
 * case.
 *
 * Now means exactly what it says — no approval exists at all (68 of 169
 * production cases). Before `budgetStartUnknown` split out separately, this
 * flag also covered approved-but-undated cases and over-claimed ~135.
 */
export const WAITING_ON_DATA_GATHERING_LABEL = 'Waiting on data gathering'

/**
 * `row.caseKind === 'CHANGE_REQUEST'`'s badge copy (sc-1212 round 3).
 *
 * Plain words, not the wire enum — a reader shouldn't have to know what
 * `CHANGE_REQUEST` means. Phrased as what happened ("a plan was delivered
 * and someone asked for a change"), not as a warning: this is a fact about
 * the KIND of work on the row, not a problem like
 * `WaitingOnDataGatheringBadge` flags. See `ForecastQueueCaseKind`'s doc
 * comment for why this is a badge/filter rather than a fourth section.
 */
export const CHANGE_REQUEST_BADGE_LABEL = 'Change to a delivered plan'

/**
 * `row.caseWorkKind === 'FINANCIAL_PLANNING'`'s badge copy (sc-1212 round 5).
 *
 * "Financial Planning" everywhere user-facing, never "Ongoing" / "IE/FP" —
 * see `ANALYTICS_WORK_KIND_LABELS`. Rendered with the `idle` tone, same as
 * `ChangeRequestBadge`: this is a fact about what KIND of work the row is,
 * not a problem with it — amber would make 14 ordinary cases look like they
 * need attention.
 */
export const FINANCIAL_PLANNING_BADGE_LABEL = 'Financial Planning'

/**
 * How long the change currently open on a `CHANGE_REQUEST` case has been
 * open — shown in the Age column in place of `formatBudgetDaysLeft` (see
 * `ForecastQueueRow.changeRequestAgeDays`'s doc comment for why: the
 * turnaround budget covers BUILDING a plan, that plan already shipped, and
 * the budget line always reads "blown" on a case that's 76-122 days old).
 *
 * `null` means the request date itself wasn't recoverable — said plainly
 * rather than silently falling back to the (wrong) budget line.
 */
export function formatChangeRequestAge(days: number | null): string {
  if (days === null) return 'Change date unknown'
  if (days === 0) return 'Requested today'
  return days === 1 ? '1 day since requested' : `${days} days since requested`
}

/** Mirrors the API's `AUDIT_QUIET_DAYS` (`apps/api/.../analytics/lib/queue-staleness.ts`)
 * — the window inside which an audit still counts as "recently handled" for
 * the marker below. There is no shared package between web and api for this
 * one constant, so it's copied rather than imported, same as the rest of this
 * file mirrors API types by hand. */
const AUDIT_QUIET_DAYS = 5

export type AuditMarkerState = 'attention' | 'fresh' | 'stale' | 'never'

/**
 * Which of the four marker states this row is in.
 *
 * Extracted and exported so the states can be exercised without rendering a
 * table — the ordering below is the whole behaviour, and it is the kind of
 * chain that reads correctly while being wrong. `attention` is tested FIRST
 * and on its own: a case can be flagged while also having been audited
 * yesterday (the API requires both staleness and audit age, but a case that
 * goes stale again after an old audit arrives here with a recent-looking
 * count), and in that case the flag is what the manager needs to see.
 */
export function auditMarkerState(
  row: Pick<CaseAuditTarget, 'needsAttention' | 'daysSinceAudit'>,
): AuditMarkerState {
  // `needsAttention` is absent on the delivered rows rather than false: they
  // have no snapshot history, so the resurfacing rule has nothing to say about
  // them. Undefined reads the same as not flagged here, which is correct —
  // what it must NOT do is report them as stale on the strength of a missing
  // field.
  if (row.needsAttention) return 'attention'
  if (row.daysSinceAudit === null) return 'never'
  return row.daysSinceAudit < AUDIT_QUIET_DAYS ? 'fresh' : 'stale'
}

/**
 * Colour per audit-marker state, off the shared `health*` ramp — the same
 * mapping shape as {@link FORECAST_QUEUE_BAND_TONE}, and for the same reason:
 * the console has exactly one green, one amber and one red, and a literal
 * `emerald-600` here would drift from them the first time either moves.
 *
 * **`never` and `stale` are deliberately the SAME `idle` grey.** "Nobody has
 * looked" and "the last look has gone cold" are the same instruction to a
 * manager scanning the column; two different greys would make the reader hunt
 * for a distinction that does not exist. The hover card spells out which one
 * it actually is.
 *
 * `fresh` is the only `healthy` reading, and it is the point of the marker:
 * a manager has to be able to see which rows they already handled rather than
 * re-auditing on a hunch.
 */
export const AUDIT_MARKER_TONE: Record<AuditMarkerState, HealthVariant> = {
  attention: 'error',
  fresh: 'healthy',
  stale: 'idle',
  never: 'idle',
}

/**
 * The marker's own words, shown BESIDE the icon (sc-1212 round 9).
 *
 * Tyler, 2026-10-06: *"its a small clickable icon and not clear the audited or
 * chased and just kinda clunky."* A glyph alone cannot say which of four states
 * it is in, and the colour ramp deliberately gives `stale` and `never` the same
 * grey — so the only way to tell them apart was to hover every row one at a
 * time. The label is the fix; the hover card goes back to being for the note.
 *
 * Deliberately short enough not to re-widen the row, which is why the audit
 * control became an icon in the first place.
 */
export const AUDIT_MARKER_LABELS: Record<AuditMarkerState, string> = {
  attention: 'Look',
  fresh: 'Done',
  stale: 'Stale',
  never: 'Audit',
}

/**
 * Copy and tone for each reason a case has no delivery meeting (sc-1212 round
 * 7). `null` is absent on purpose: a banded row has no reason to show.
 *
 * **Only one of the three is a problem**, and the tones say so. 79 of the 84
 * undated cases are `NEVER_BOOKED` — 64 of them still in DG Incomplete, where
 * no delivery meeting SHOULD exist — so colouring that bucket would put an
 * alarm on half the open book and teach a reader to ignore the column.
 * `DELIVERED_NOTHING_BOOKED` is 2 cases, one of them untouched for six months.
 */
export const UNSCHEDULED_REASON_LABELS: Record<
  Exclude<UnscheduledReason, null>,
  string
> = {
  DELIVERED_NOTHING_BOOKED: 'Missing required meetings',
  CANCELLED_NOT_REBOOKED: 'Meeting cancelled — rebook',
  // Says what IS booked, not what is missing. A case owes no work before the
  // meeting that OPENS its engagement, so this row needs no action — it is
  // here to stop the reader mistaking it for one that does.
  //
  // **"Opening meeting", not "IE kickoff", since round 24.** Two engagements
  // now reach this reason: an `IE Kickoff Meeting` and a `Financial Planning
  // Data Gathering`. The row already carries `kickoffMeetingType`, so the
  // specific label is on screen anyway and the badge does not have to name one
  // engagement and be wrong for the other. No FP intake is booked on
  // production today, so this is correctness ahead of the population rather
  // than a fix to something visibly broken.
  KICKOFF_BOOKED: 'Opening meeting booked — no work due yet',
  // Says what IS true: the second service is queued behind the first, so
  // nothing is late and nothing is missing.
  AWAITING_SECOND_SERVICE: 'Awaiting second service — worked after Rothology',
  NEVER_BOOKED: 'No meeting booked yet',
}

export const UNSCHEDULED_REASON_TONE: Record<
  Exclude<UnscheduledReason, null>,
  HealthVariant
> = {
  DELIVERED_NOTHING_BOOKED: 'error',
  CANCELLED_NOT_REBOOKED: 'action',
  // `healthy`, and deliberately the only reason that is: every other value
  // here names something to chase, and this one names something already done.
  KICKOFF_BOOKED: 'healthy',
  // `idle`, not `healthy`: nothing has been DONE here (that is what earns
  // `healthy`) and nothing is to be CHASED (that is what earns `action`). The
  // Pillars half is simply queued behind the Rothology half, the same
  // not-yet-started reading `NEVER_BOOKED` carries.
  AWAITING_SECOND_SERVICE: 'idle',
  NEVER_BOOKED: 'idle',
}

/**
 * What each reason means, for the row's hover text — a sentence, not a
 * restatement of the label, following `ForecastOffsetsHint`'s rule that a
 * reader should learn what the number claims rather than see it twice.
 */
export const UNSCHEDULED_REASON_HINTS: Record<
  Exclude<UnscheduledReason, null>,
  string
> = {
  DELIVERED_NOTHING_BOOKED:
    'This plan has already been delivered and there is no next meeting on the calendar. Until one is booked there is no date for the work to aim at, and nothing will flag it as late. Shown only once five business days have passed since delivery, so a case delivered this morning is not flagged.',
  CANCELLED_NOT_REBOOKED:
    'A delivery meeting was on the calendar and was cancelled, with nothing booked in its place. A cancelled meeting sets no deadline, so this case will sit here until someone rebooks it.',
  KICKOFF_BOOKED:
    'A case whose engagement-opening meeting is already booked — an IE kickoff with the planner, or a Financial Planning data gathering. It sets no deadline because nothing has to be built before it: it is where the engagement starts. Shown so this case is not mistaken for one with nothing on the calendar, which would send it to Planning to chase a meeting that already exists.',
  AWAITING_SECOND_SERVICE:
    'This is the Pillars half of a Strategic Oversight sale. Selling it opens two cases and the Pillars one is worked only after the Rothology one closes, so it has no meeting yet because its turn has not come — not because one was missed. Nothing is late here; it needs no chasing until the Rothology case is done.',
  NEVER_BOOKED:
    'No delivery meeting has ever been booked for this case. Usually this is correct — most of these are still waiting on the client to finish data gathering, which is what creates the meeting. On an IE case it means not even the kickoff is booked, which is the one to escalate to Planning.',
}

/**
 * Resolvers over the three maps above. **Use these, never index the maps.**
 *
 * The API and web bundles deploy separately, so a bundle can run against an
 * API already sending a reason it has never heard of; `MAP[reason]` is then
 * `undefined` and rendering it is the React #130 white-screen `REOPENED` caused
 * on 2026-10-07. The maps keep their compile-time exhaustiveness; these cover
 * the deploy window. Same contract as `cleaningKindPresentation`.
 *
 * Unknown reason: the label falls through to the raw value so a reader can
 * report exactly what arrived, the tone is neutral `idle`, and the hint
 * commits to nothing a newer API might contradict.
 */
export function unscheduledReasonLabel(reason: string): string {
  const known: Record<string, string | undefined> = UNSCHEDULED_REASON_LABELS
  return known[reason] ?? reason
}

export function unscheduledReasonTone(reason: string): HealthVariant {
  const known: Record<string, HealthVariant | undefined> =
    UNSCHEDULED_REASON_TONE
  return known[reason] ?? 'idle'
}

export function unscheduledReasonHint(reason: string): string {
  const known: Record<string, string | undefined> = UNSCHEDULED_REASON_HINTS
  return (
    known[reason] ??
    'This case has no delivery meeting for a reason this version of the page does not recognise. Nothing here claims it is late.'
  )
}

/**
 * `row.serviceTypeMissing`'s badge (sc-1212 round 7, Tyler 2026-10-05).
 *
 * 129 live cases have no `service_type`, which puts them outside the Experience
 * picker and every per-experience rollup — so the gap is invisible exactly
 * where someone would look for it. Flagged on the row, which is where it is
 * fixable.
 *
 * `action` amber, not `error` red: nothing is wrong with the WORK, a field is
 * unfilled. Red would rank it alongside a missed deadline.
 */
export const SERVICE_TYPE_MISSING_LABEL = 'No service type'

/**
 * Copy and tone for the submit window (sc-1212 round 8).
 *
 * **CLOSED is amber, not red.** It is the majority state — 108 of 146 on
 * production — and it describes a booking pattern rather than a failure: the
 * Rothology deliveries are booked a median seven days out while the arithmetic
 * needs about nineteen. Red on three quarters of a list is an alarm nobody can
 * act on, and it would read as blame aimed at planners.
 *
 * `OPEN` is `idle`, not `healthy`: having room is the unremarkable state, and a
 * green tick on it would imply something had been achieved.
 *
 * **The words deliberately differ from the state names, because "closed" is
 * taken.** On this page it already means a finished ticket — stage
 * `1058207545`, 909 of them, labelled "Closed" by `stageLabelFor` in this very
 * file — and it is also the root of the "Ready to close" tab and of
 * `CloseOutState`'s close-out reading. Four senses of one word, and Tyler hit
 * the collision reading a row: *"I also dont understand the closed part? We use
 * the word closed to say the Ticket status is closed this is completely
 * done."* So the window speaks of a **cutoff**: the last day a revision could
 * arrive and still be delivered before the meeting.
 *
 * The `SubmitWindowState` values stay `OPEN | CLOSING | CLOSED`. They are a
 * wire contract shared with the API, renaming them changes nothing on screen,
 * and this map is the one place the two vocabularies have to meet.
 */
export const SUBMIT_WINDOW_LABELS: Record<SubmitWindowState, string> = {
  OPEN: 'Before cutoff',
  CLOSING: 'Cutoff near',
  CLOSED: 'Past cutoff',
}

export const SUBMIT_WINDOW_TONE: Record<SubmitWindowState, HealthVariant> = {
  OPEN: 'idle',
  // `action`, NOT `error`, which is what the docblock above has asked for since
  // round 8 while the code said otherwise — 98 of 159 rows rendered red, the
  // exact "alarm nobody can act on" that comment rules out. Fixed 2026-10-20.
  CLOSING: 'action',
  CLOSED: 'action',
}

/**
 * The planner-response states (Awaiting planner tab): label and tone.
 *
 * One map for the chip, the group heading and the Status column, so they cannot
 * disagree about what a state is called. Tone: `JUST_HELD` idle (inside the
 * grace period, nothing to do), `CHASE` action, `LATE` and `STALLED` error --
 * unlike the cutoff, this clock IS the planner's, so red is a fair reading.
 */
export const PLANNER_RESPONSE_LABELS: Record<PlannerResponseState, string> = {
  JUST_HELD: 'Just held',
  CHASE: 'Chase',
  LATE: 'Late',
  STALLED: 'Stalled',
}

export const PLANNER_RESPONSE_TONE: Record<
  PlannerResponseState,
  HealthVariant
> = {
  JUST_HELD: 'idle',
  CHASE: 'action',
  LATE: 'error',
  STALLED: 'error',
}

const PLANNER_RESPONSE_STATES: ReadonlyArray<string> = [
  'JUST_HELD',
  'CHASE',
  'LATE',
  'STALLED',
]

/**
 * The state to render, or null. Null for `null`, for absent (an older API) and
 * for a value this build does not know -- all three mean "no state to show",
 * and none of them may be guessed into one.
 */
export function plannerResponseOf(row: {
  plannerResponse?: string | null
}): PlannerResponseState | null {
  const value = row.plannerResponse
  return value && PLANNER_RESPONSE_STATES.includes(value)
    ? (value as PlannerResponseState)
    : null
}

/**
 * "met Oct 7 · 8 business days" -- the figure a reader quotes when chasing.
 * Either half is dropped when the row cannot back it; null with neither.
 */
export function plannerWaitLine(row: {
  lastHeldMeetingAt?: string | null
  plannerWaitedBusinessDays?: number | null
}): string | null {
  const parts: Array<string> = []
  if (row.lastHeldMeetingAt) {
    const date = new Date(row.lastHeldMeetingAt)
    if (!Number.isNaN(date.getTime())) {
      parts.push(
        `met ${date.toLocaleDateString('en-US', {
          timeZone: REPORTING_TIME_ZONE,
          month: 'short',
          day: 'numeric',
        })}`,
      )
    }
  }
  const waited = row.plannerWaitedBusinessDays
  if (typeof waited === 'number' && Number.isFinite(waited)) {
    parts.push(formatBusinessDays(waited))
  }
  return parts.length > 0 ? parts.join(' · ') : null
}

/**
 * What the revision-cutoff marker says about a Revisions expected row
 * (sc-1212, Forecasts restructure).
 *
 * Pure, and kept here rather than in the component file (`apps/web/CLAUDE.md`).
 * Every line is `null` when the row cannot back it, and the marker prints only
 * the lines it has: **a missing figure is no claim at all, never a blank and
 * never a zero.**
 *
 * - `cutoffLine` needs `submitWindow.submitBy`. A stage with too few
 *   observations publishes no offset, so the window has `requiredReadyBy` but
 *   no cutoff; the line then says why, rather than inventing a date.
 * - `daysLine` needs `daysUntilClose`, which is null exactly when `submitBy` is.
 * - `rateLine` needs `workExpectancyPct`, which is null for the IE book (28 of
 *   158 rows on 2026-10-20): no rate has been measured for those meetings. The
 *   API's `work-expectancy.ts` is emphatic that the figure is a base rate for a
 *   KIND of meeting and never a prediction about one case, so the sentence says
 *   so every time it appears.
 */
export interface RevisionCutoffReading {
  state: SubmitWindowState
  /** `Before cutoff` / `Cutoff near` / `Past cutoff`. */
  stateLabel: string
  cutoffLine: string
  daysLine: string | null
  rateLine: string | null
}

export const REVISION_CUTOFF_EXPLANATION =
  'The last day a revision could arrive and still be delivered a business week before the meeting. Not a missed work order: no revision is owed yet.'

export function revisionCutoffReading(row: {
  submitWindow?: {
    submitBy: string | null
    state: SubmitWindowState
    daysUntilClose: number | null
  } | null
  workExpectancyPct?: number | null
}): RevisionCutoffReading | null {
  const window = row.submitWindow
  // No window means no meeting to work back from: nothing to say.
  if (!window) return null

  const cutoffLine = window.submitBy
    ? `Cutoff ${formatReportingDate(window.submitBy)}.`
    : 'No cutoff date: too few past cases to measure how long this work takes.'

  const days = window.daysUntilClose
  let daysLine: string | null = null
  if (typeof days === 'number' && Number.isFinite(days)) {
    if (days > 0) daysLine = `${formatDays(days)} left.`
    else if (days === 0) daysLine = 'Today is the last day.'
    else daysLine = `Passed ${formatDays(-days)} ago.`
  }

  const pct = row.workExpectancyPct
  const rateLine =
    typeof pct === 'number' && Number.isFinite(pct)
      ? `Historically ${Math.round(pct)}% of cases took a revision before a meeting of this type. A rate for the kind of meeting, not a forecast for this case.`
      : null

  return {
    state: window.state,
    stateLabel: SUBMIT_WINDOW_LABELS[window.state],
    cutoffLine,
    daysLine,
    rateLine,
  }
}

/**
 * Copy for the close-out reading (sc-1212 round 8).
 *
 * `UNKNOWN` has no label on purpose — a case that is not on the Rothology
 * three-meeting sequence (an IE case) has no close-out state to report, and
 * printing "unknown" would invent a problem out of a different service.
 */
export const CLOSE_OUT_LABELS: Record<
  Exclude<CloseOutState, 'UNKNOWN'>,
  string
> = {
  READY_TO_CLOSE: 'Escape plan held — ready to close',
  NO_ESCAPE_PLAN_EVER: 'No escape plan meeting yet',
}

export const CLOSE_OUT_TONE: Record<
  Exclude<CloseOutState, 'UNKNOWN'>,
  HealthVariant
> = {
  // Done is a good state: the only thing left is the clerical act of closing.
  READY_TO_CLOSE: 'healthy',
  // The large, normal bucket — 1,151 of 1,190. Never coloured, for the same
  // reason NEVER_BOOKED is not: an alarm on the majority is not a signal.
  NO_ESCAPE_PLAN_EVER: 'idle',
}

// ---------------------------------------------------------------------------
// Billable — work done after delivery (sc-1212 round 14)
// ---------------------------------------------------------------------------

/**
 * The rate to multiply the measured hours by.
 *
 * **The payload's rate wins, always.** The money a manager reads has to be the
 * money the server computed against, or two people quoting this page disagree
 * about an invoice. `POST_DELIVERY_HOURLY_RATE` is only reached when the
 * payload has rows and no usable rate at all — an API older than the field, or
 * a zero/NaN that would silently price every case at nothing.
 *
 * This is the same hazard as the sequence-health `Record`: a separately
 * deployed API can hand over something this bundle's type says cannot happen.
 * Here the failure is quiet rather than fatal, which makes it worse — a column
 * of `$0` beside 52 hours reads as "nothing owed".
 */
export function postDeliveryHourlyRate(
  rate: number | null | undefined,
): number {
  if (typeof rate !== 'number' || !Number.isFinite(rate) || rate <= 0) {
    return POST_DELIVERY_HOURLY_RATE
  }
  return rate
}

/**
 * Measured hours times the rate — the "billable candidate" figure.
 *
 * **`reworkRequests` is not an argument, and that is the whole design.** A
 * rework request has no measured duration, so there is nothing to price; a
 * signature that could not accept it cannot accidentally be made to. See
 * {@link postDeliveryWorkTotals}, which sums the two side by side and never
 * into each other.
 *
 * Rounded to whole dollars because that is what `formatCurrencyNumber` prints
 * anyway — rounding here means the column total equals the sum of the column,
 * rather than the rounded sum of unrounded rows, which is how a reviewed list
 * ends up a dollar out from its own footer.
 */
export function postDeliveryBillableCandidate(
  hours: number,
  hourlyRate: number,
): number {
  if (!Number.isFinite(hours) || hours <= 0) return 0
  return Math.round(hours * postDeliveryHourlyRate(hourlyRate))
}

/**
 * What the whole billable list adds up to, for the tiles above it.
 *
 * `hours` and `reworkRequests` are summed INDEPENDENTLY and the money comes
 * off the hours alone. The one assertion worth having in a test is that adding
 * a rework-only row moves `reworkRequests` and nothing else.
 */
export interface PostDeliveryWorkTotals {
  cases: number
  extraMeetings: number
  hours: number
  /** Measured hours at the payload's rate. Never includes rework. */
  billableCandidate: number
  /** Beside the hours, never inside them. */
  reworkRequests: number
  /** Why `hours` is a floor — the count of meetings with no end time. */
  meetingsMissingDuration: number
  /**
   * Cases carrying post-delivery work and NO measured time.
   *
   * Reported because `cases` would otherwise overstate what the money covers:
   * on production 41 cases carry $24,750, and the rework-only rows sit in that
   * count at zero. A reader dividing the money by the case count has to be
   * able to see why the arithmetic does not come out.
   */
  casesWithoutMeasuredHours: number
}

export function postDeliveryWorkTotals(
  rows: ReadonlyArray<{
    extraMeetings: number
    hours: number
    reworkRequests: number
    meetingsMissingDuration: number
  }>,
  hourlyRate: number,
): PostDeliveryWorkTotals {
  const rate = postDeliveryHourlyRate(hourlyRate)
  const hours = rows.reduce((sum, row) => sum + row.hours, 0)
  return {
    cases: rows.length,
    extraMeetings: rows.reduce((sum, row) => sum + row.extraMeetings, 0),
    hours,
    billableCandidate: postDeliveryBillableCandidate(hours, rate),
    reworkRequests: rows.reduce((sum, row) => sum + row.reworkRequests, 0),
    meetingsMissingDuration: rows.reduce(
      (sum, row) => sum + row.meetingsMissingDuration,
      0,
    ),
    casesWithoutMeasuredHours: rows.filter(
      (row) => !postDeliveryHasMeasuredHours(row),
    ).length,
  }
}

/**
 * Whether this row has any measured time behind it.
 *
 * **False is a real state, not a broken row.** A case reaches this list on its
 * change requests alone: Tyler's reference case 46490393746 had its escape
 * plan on 2026-09-14, a change request four hours later, and the second escape
 * plan booked for 2026-10-14 and not yet held. Zero held meetings, zero
 * measured hours, and still the case he pointed at when asking for the tab.
 *
 * The one place this must be consulted is the money: a `$0` rendered as though
 * it were a priced answer is the same quiet failure `postDeliveryHourlyRate`
 * guards against. Nothing to price is not the same claim as nothing owed.
 */
export function postDeliveryHasMeasuredHours(row: {
  hours: number
  extraMeetings: number
}): boolean {
  return Number.isFinite(row.hours) && row.hours > 0 && row.extraMeetings > 0
}

/**
 * The next occurrence of a delivery step that has already happened once — the
 * booked second escape plan.
 *
 * **The only signal of future work on a row with no hours**, which is why it
 * is pulled out rather than left to the sequence hover card: a case whose
 * whole story is "a change request landed and the next meeting is booked" has
 * nothing in the hours or the money columns to say so.
 *
 * Deliberately never priced. 137 of 520 escape plans are cancelled, so about a
 * quarter of money billed off a booking would never happen — this reports the
 * date and stops.
 *
 * Tolerant of a missing `steps` array, because `sequence` crosses the same
 * deploy boundary as everything else on this row.
 */
export function postDeliveryBookedRepeat(sequence: {
  steps?: ReadonlyArray<{ label: string; repeatAt: string | null }>
}): { label: string; at: string } | null {
  const steps = sequence.steps ?? []
  for (const step of steps) {
    if (step.repeatAt) return { label: step.label, at: step.repeatAt }
  }
  return null
}

/**
 * Case Design stage ids, paired with the labels the API resolves them to.
 *
 * Mirrored as literals for the same reason `CASE_DESIGN_STAGES` is
 * (`forecast-stage-pills.ts`):
 * the API's `CASE_DESIGN_STAGES` is not reachable from `apps/web`, and the ids
 * are identical in both HubSpot portals.
 *
 * **Only the two stopped stages are listed, because only they are a reading.**
 * This map does not exist to re-label every stage — the API already sends
 * display text — it exists so `postDeliveryStageReading` can answer "is this
 * case still open" whichever spelling arrives.
 */
const POST_DELIVERY_STOPPED_STAGES: Record<string, string> = {
  '1058207545': 'Closed',
  '1294418254': 'Refund Review Sent',
}

/** How a stage reads on the billable list. */
export interface PostDeliveryStageReading {
  /** Display text — the resolved label when recognised, else what arrived. */
  label: string
  /**
   * The case has STOPPED (closed, or in refund review).
   *
   * Not an error and not healthy: a closed case still earned its hours, so the
   * figure stands — but nobody is going to add to it, and the manager's next
   * move is different. `idle` for stopped, `info` for still open.
   */
  stopped: boolean
  tone: HealthVariant
}

/**
 * Resolve `PostDeliveryWorkRow.stage` for display.
 *
 * **Never a bare `Record` lookup on a wire string.** `stage` arrives from a
 * separately deployed API that may send an id or a label (the field is named
 * `stage`, not `stageLabel`), and a stage HubSpot added yesterday is in
 * neither spelling this bundle knows. So:
 *
 * - a recognised id resolves to its label,
 * - a recognised label is matched case-insensitively and kept,
 * - anything else is shown verbatim and read as still open.
 *
 * Showing the raw value is the same choice `caseSequenceHealthPresentation`
 * and `MeetingOutcomeCell` make: ugly and honest beats a blank cell, and far
 * beats inventing "Closed" for a stage nobody has styled yet.
 */
export function postDeliveryStageReading(
  stage: string | null,
): PostDeliveryStageReading {
  if (!stage) {
    // Not "Open". A missing stage is a gap in the ticket mirror, and claiming
    // the case is open on the strength of an absence is the kind of guess this
    // list cannot afford — it is the input to an invoice.
    return { label: 'Stage unknown', stopped: false, tone: 'idle' }
  }

  const byId = POST_DELIVERY_STOPPED_STAGES[stage]
  if (byId) return { label: byId, stopped: true, tone: 'idle' }

  const normalised = stage.trim().toLowerCase()
  const byLabel = Object.values(POST_DELIVERY_STOPPED_STAGES).find(
    (label) => label.toLowerCase() === normalised,
  )
  if (byLabel) return { label: byLabel, stopped: true, tone: 'idle' }

  return { label: stage, stopped: false, tone: 'info' }
}

/* ---------------------------------------------------------------------------
 * The planner rollup — Tyler's "repeat offenders" (sc-1212 round 15)
 *
 * Tyler, 2026-10-07: *"knowing who the repeat offenders are and how much they
 * are costing us would be great."*
 *
 * Everything here is about ONE distinction, because getting it wrong has
 * already produced a wrong answer out loud: **the planner is not the ticket
 * owner.** The owner on these rows is the Case Design specialist. A planner's
 * change requests landing on a specialist's name would blame the wrong job for
 * the wrong work, so the rollup is grouped on `planner` and labelled as such
 * everywhere it appears.
 * ------------------------------------------------------------------------- */

/** How a planner was attributed, as a manager reads it. */
export interface PostDeliveryPlannerSourceReading {
  /** Display text — a resolved phrase when recognised, else the raw value. */
  label: string
  /**
   * Attributed from the legacy `rothology_planner` property, which resolved to
   * a TICKET OWNER (sc-1175 replaced it). **The name on a legacy row may be a
   * specialist**, so a figure resting on it is weaker evidence than one
   * attributed from a meeting. 24 of 54 production rows.
   */
  legacy: boolean
  /** Whether this bundle recognised the value at all — see the resolver. */
  recognised: boolean
  /** One sentence, for a `title` / tooltip at the point of the figure. */
  hint: string
}

/**
 * `satisfies Record<PlannerSource, …>`, not an annotation — the same shape
 * `HEALTH_PRESENTATION` uses. It buys completeness in BOTH directions: adding a
 * member to the mirrored `PlannerSource` union fails to compile until it is
 * labelled here, and the resolver below still widens to `string` to survive a
 * member the union itself has not caught up with.
 */
const PLANNER_SOURCE_READING = {
  HUBSPOT_PROPERTY: {
    label: 'Legacy ticket property',
    legacy: true,
    recognised: true,
    hint: "Attributed from the legacy rothology_planner property, which resolved to the TICKET OWNER — so this name may be a Case Design specialist rather than the client's planner. sc-1175 replaced it with meeting-based attribution and shipped no backfill, so the name changes once a meeting syncs for this client.",
  },
  DATA_GATHERING: {
    label: 'Data gathering meeting',
    legacy: false,
    recognised: true,
    hint: 'Attributed from the planner who held the data gathering meeting.',
  },
  PRESENTATION: {
    label: 'Presentation meeting',
    legacy: false,
    recognised: true,
    hint: 'Attributed from the planner who held the presentation.',
  },
  ESCAPE_PLAN_MEETING: {
    label: 'Escape plan meeting',
    legacy: false,
    recognised: true,
    hint: 'Attributed from the planner who held the escape plan — the delivery this list measures the extra work against.',
  },
  MANUAL: {
    label: 'Set by hand',
    legacy: false,
    recognised: true,
    hint: 'Set by hand on the client record rather than derived from a meeting.',
  },
} satisfies Record<PlannerSource, PostDeliveryPlannerSourceReading>

/**
 * Resolve `PostDeliveryWorkRow.plannerSource` for display.
 *
 * **Takes `string`, not `PlannerSource`** — the signature says out loud that
 * the caller is handing over whatever the API sent, exactly like
 * `caseSequenceHealthPresentation`. `plannerSource` is a Prisma enum behind a
 * separately deployed API: a sixth member shipped server-side arrives in this
 * bundle before this map knows it, and `PLANNER_SOURCE_READING[source].label`
 * on an unrecognised key throws inside render — the React #130 white screen
 * this page has already shipped once.
 *
 * An unrecognised value is shown VERBATIM and reported as `recognised: false`.
 * It is deliberately NOT marked `legacy`: legacy is a specific claim about the
 * old ticket-owner property, and asserting it about a source nobody has seen
 * would put a "this may be a specialist" warning on a row we know nothing
 * about. `recognised: false` is the honest flag, and the UI says "unrecognised"
 * rather than guessing either way.
 *
 * `null` is a real and different state: the client has no planner attributed at
 * all. One production case, carrying the largest single figure on the list.
 */
export function postDeliveryPlannerSourceReading(
  source: string | null | undefined,
): PostDeliveryPlannerSourceReading {
  if (!source) {
    return {
      label: 'No planner attributed',
      legacy: false,
      recognised: true,
      hint: "This client has no planner on record, so the work is reported unattributed rather than added to somebody else's total.",
    }
  }

  // Widened to `string` keys on purpose, exactly as
  // `caseSequenceHealthPresentation` does: indexing the map directly would let
  // the compiler promise a reading for a wire value it has never seen.
  const known: Record<string, PostDeliveryPlannerSourceReading | undefined> =
    PLANNER_SOURCE_READING
  const match = known[source]
  if (match) return match

  return {
    label: source,
    legacy: false,
    recognised: false,
    hint: `This bundle does not recognise the attribution source "${source}", so it is shown as it arrived. The planner name is still the one the API sent; how it was decided cannot be judged here.`,
  }
}

/** One planner's share of the post-delivery work, with its money. */
export interface PostDeliveryPlannerRollupRow {
  /**
   * Stable list key. The planner id, or `UNATTRIBUTED` for the null bucket —
   * never the array index, because the rows reorder as filters change.
   */
  key: string
  /** Null is the unattributed bucket, which is never hidden or merged. */
  planner: { id: string; userId: string | null; name: string } | null
  cases: number
  hours: number
  /**
   * `hours` at the section's rate. **A candidate, not an amount owed** — the
   * same caveat as every other money figure on this list, and the reason the
   * rollup's column is captioned rather than left to read as an invoice total.
   */
  billableCandidate: number
  /** Beside the hours, never inside them — and never in the money. */
  reworkRequests: number
  /** Of `cases`, how many rest on the legacy ticket-owner attribution. */
  legacyAttributed: number
}

export interface PostDeliveryPlannerRollup {
  /**
   * Whether the payload carries planner attribution AT ALL.
   *
   * False when no row has the field — `apps/web` and `apps/api` deploy
   * separately, so this view can render against a payload that predates the
   * contract. The rollup is then omitted rather than shown with every case in
   * the unattributed bucket, which would read as "54 cases have no planner"
   * when the truth is "this API has not shipped yet".
   */
  available: boolean
  /** Most hours first, the unattributed bucket last whatever its size. */
  rows: Array<PostDeliveryPlannerRollupRow>
}

/** The null bucket's list key — not a planner id any row can collide with. */
export const POST_DELIVERY_UNATTRIBUTED_KEY = 'UNATTRIBUTED'

/**
 * Total the visible rows per planner.
 *
 * **Computed over the rows ON SCREEN, and the only place it is computed.** The
 * API briefly shipped a `byPlanner` rollup too; it was removed rather than left
 * unread, because this page fetches the queue once and filters in the browser,
 * so a server-side rollup describes the unfiltered population — and a rollup
 * that disagrees with the list beneath it is worse than no rollup at all. If
 * the API ever grows one again for another consumer, it must not be rendered
 * above a filtered table.
 *
 * `planner` and `plannerSource` are OPTIONAL in the parameter type on purpose:
 * it lets the helper tell "the API sent null" from "the API never sent the
 * field", which is what `available` reports — and it is the same deploy-skew
 * tolerance `postDeliveryBookedRepeat` takes on `sequence.steps`.
 */
export function postDeliveryPlannerRollup(
  rows: ReadonlyArray<{
    hours: number
    reworkRequests: number
    planner?: { id: string; userId: string | null; name: string } | null
    plannerSource?: string | null
  }>,
  hourlyRate: number,
): PostDeliveryPlannerRollup {
  const rate = postDeliveryHourlyRate(hourlyRate)
  const available = rows.some((row) => row.planner !== undefined)
  if (!available) return { available: false, rows: [] }

  const buckets = new Map<string, PostDeliveryPlannerRollupRow>()
  for (const row of rows) {
    const planner = row.planner ?? null
    const key = planner ? planner.id : POST_DELIVERY_UNATTRIBUTED_KEY
    const bucket = buckets.get(key) ?? {
      key,
      planner,
      cases: 0,
      hours: 0,
      billableCandidate: 0,
      reworkRequests: 0,
      legacyAttributed: 0,
    }
    bucket.cases += 1
    bucket.hours += Number.isFinite(row.hours) ? row.hours : 0
    bucket.reworkRequests += row.reworkRequests
    if (postDeliveryPlannerSourceReading(row.plannerSource).legacy) {
      bucket.legacyAttributed += 1
    }
    buckets.set(key, bucket)
  }

  const rollup = [...buckets.values()].map((bucket) => ({
    ...bucket,
    // Priced off the planner's SUMMED hours, never by adding up per-case
    // money: rounding each case first and then summing is how a rollup ends
    // up a dollar away from the column it is meant to describe.
    billableCandidate: postDeliveryBillableCandidate(bucket.hours, rate),
  }))

  rollup.sort((a, b) => {
    // The unattributed bucket is pinned last rather than ranked. It is not a
    // person and it must not read as the firm's best or worst planner — but it
    // is never dropped either: it holds the single largest case on the list.
    const aUnattributed = a.planner === null
    const bUnattributed = b.planner === null
    if (aUnattributed !== bUnattributed) return aUnattributed ? 1 : -1
    if (b.hours !== a.hours) return b.hours - a.hours
    if (b.cases !== a.cases) return b.cases - a.cases
    // A deterministic last resort, so two planners tied on hours and cases
    // don't swap places between renders of the same data.
    return (a.planner?.name ?? '').localeCompare(b.planner?.name ?? '')
  })

  return { available: true, rows: rollup }
}

/**
 * How many planners the rollup shows before the tail is folded away.
 *
 * Six, from the production shape: twelve planners, of whom four carry 7h or
 * more and the rest trail off to 1h and below. A list that shows all twelve at
 * once buries the four a manager is looking for.
 */
export const POST_DELIVERY_PLANNER_ROLLUP_VISIBLE = 6

export interface PostDeliveryPlannerRollupSplit {
  shown: Array<PostDeliveryPlannerRollupRow>
  hidden: Array<PostDeliveryPlannerRollupRow>
  /**
   * The largest `hours` in `hidden` — so the fold can say what it is hiding
   * ("5 more, 1 hr and below") instead of just how many rows.
   */
  hiddenHoursCeiling: number
}

/**
 * Split the rollup into the head a manager reads and the tail behind a
 * disclosure.
 *
 * **The unattributed bucket is always in `shown`, whatever its rank.** It
 * currently holds the single largest case on the list (5.0h, ~10% of the
 * money) while ranking below several planners on nothing but sort position, so
 * a plain `slice` would fold away a tenth of the figure the list exists to
 * report. That is the one rule in this function that is not about tidiness.
 */
export function postDeliveryPlannerRollupSplit(
  rows: ReadonlyArray<PostDeliveryPlannerRollupRow>,
  limit: number = POST_DELIVERY_PLANNER_ROLLUP_VISIBLE,
): PostDeliveryPlannerRollupSplit {
  const safeLimit = Number.isFinite(limit) ? Math.max(1, Math.trunc(limit)) : 1
  const shown: Array<PostDeliveryPlannerRollupRow> = []
  const hidden: Array<PostDeliveryPlannerRollupRow> = []

  for (const row of rows) {
    if (shown.length < safeLimit || row.planner === null) shown.push(row)
    else hidden.push(row)
  }

  return {
    shown,
    hidden,
    hiddenHoursCeiling: hidden.reduce(
      (max, row) => Math.max(max, row.hours),
      0,
    ),
  }
}

/**
 * What the "Short turnaround" hourglass should say, tolerant of an API that does
 * not send the fields yet (apps/web and apps/api deploy separately; a React
 * #130 white-screen on 2026-10-07 came from trusting a new field). Absent
 * `shortTurnaround` is `false`; absent or non-finite `turnaroundBusinessDays`
 * is `null` — never 0, never NaN.
 *
 * Takes optional fields on purpose: the type says required, the wire may not.
 */
export function shortTurnaroundReading(row: {
  shortTurnaround?: boolean | null
  turnaroundBusinessDays?: number | null
}): { show: boolean; runway: number | null } {
  const runway = row.turnaroundBusinessDays
  return {
    show: row.shortTurnaround === true,
    runway:
      typeof runway === 'number' && Number.isFinite(runway) && runway >= 0
        ? Math.round(runway)
        : null,
  }
}

export const SHORT_TURNAROUND_EXPLANATION =
  'The meeting was booked with less runway than this kind of work historically takes, so the case could not have met the date whoever it was assigned to. A scheduling outcome, not a specialist delay.'

/** The runway sentence, or `null` when there is no runway to state. */
export function runwaySentence(
  runway: number | null,
  budgetBusinessDays: number | null,
): string | null {
  if (runway === null) return null
  const booked = `Booked ${formatBusinessDays(runway)} out`
  if (budgetBusinessDays === null) return `${booked}.`
  // `formatBusinessDays` yields a NOUN phrase, so it has to sit in noun
  // position: "a 34 business days target" is not English. Hyphenating it here
  // instead would be a second spelling of the unit, which is what
  // `formatBusinessDays` exists to prevent.
  return `${booked}; the firm's target is ${formatBusinessDays(budgetBusinessDays)}.`
}
